<template lang="pug">
  .main-container.column.center.m-top-0
    .header.absolute
    .content
      .profile.row.middle.m-bottom-2(v-if='!editProfileMode')
        img.profile-img.m-right-3.m-bottom-1(:src='userData.avatar || defaultImg' :alt='userData.fullName')
        .column
          span.title-big.bolder.m-bottom-1.ellipsis.max-width {{ userData.fullName }}
          span.text-big.dark-gray.m-bottom-08 {{ position }}
          span.text-big.dark-gray.ellipsis.max-width {{ schoolName }}
      .profile.row.middle.m-bottom-2(v-else)
        .relative.m-right-3.m-bottom-1
          img.profile-img.width-100(
            :src='fileBase64.content ? `data:image/png;base64,${fileBase64.content}` : profileEdition.avatar || defaultImg'
            :alt='userData.fullName'
          )
          .row.center.middle.width-100.absolute.profile-image-container
            label.pointer(
              for='profileImage'
              v-tooltip.bottom='{ content: $t("profile.changePhoto"), classes: "profile-user-tooltip" }'
            )
              simple-svg.dark-gray-hover-blue(:filepath='editIcon' :width='"28px"' :height='"28px"')
              input.hidden-input(ref='profileImageInput' id='profileImage' type='file' @change='handleChangeImage' accept="image/png,image/jpg,image/jpeg")
            button.m-left-3(
              v-if='profileEdition.avatar || fileBase64.content'
              @click='cleanImage'
              type='button'
              v-tooltip.bottom='{ content: $t("profile.deletePhoto"), classes: "profile-user-tooltip" }'
            )
              simple-svg.dark-gray-hover-blue(:filepath='trashIcon' :width='"28px"' :height='"28px"')
        .column.width-100
          .column
            .column.full-name-container
              span.text-normal.m-bottom-1
                | {{ $t('profile.personalInfo') }}
              input.input-edit.edit.text-normal.dark-gray(
                :value='profileEdition.fullName'
                name='fullName'
                @input='updateValue'
                :maxlength='fullNameMaxLength'
              )
              .row.m4-top-2(v-show='missingFullName')
                simple-svg.red-icon.m-right-1(:filepath='stopIcon' :width='"16px"' :height='"16px"')
                span.error-desc
                  | {{ $t('errors.nameRequiredError') }}
            span.text-normal.m-bottom-1
              | {{ $t('profile.appointment') }}
            //- TODO change it when KSelect use id
            k-select.input-edit.text-normal.dark-gray(
              :options='positionOptions'
              :preselectedOption='preSelectedPositionId'
              name='position'
              withBorder
              edition
              @selectOption='updateSelect'
            )
      .row.space-between.middle.tabs
        .tab-selection.item-1.row.middle.relative.m-right-4
          button.item-1.center.tab-selector.text-big.row(
            v-for='tab in tabs'
            :key='tab'
            :class='{ "active-tab": activeTab === tab }'
            @click='setActiveTab(tab)'
          ) {{ $t(`profile.tabs.${tab}`) }}
        button.edit-button.secondary-button.with-icon.max-width-none(
          v-if='showEditButton'
          type='button'
          @click='goToEditView()'
          :disabled='isSchoolTrial'
          :class='{ "no-border" : isSchoolTrial }'
        )
          simple-svg.blue-icon.m4-right-2(
            :filepath='isSchoolTrial ? lockIcon : editIcon'
            :width='"20px"'
            :height='"20px"'
            :class='{ "dark-gray-icon" : isSchoolTrial}'
          )
          | {{ $t(`profile.edit.${activeTab}`)}}
        .row(v-else-if='!isTeacherInSchoolView')
          button.primary-button.m4-right-6(type='button' @click='save' :disabled='shouldDisableSave')
            | {{ $t('profile.save') }}
          button.simple-button.blue(type='button' @click='quitEditionView')
            | {{ $t('profile.cancel') }}
      .tab-container
        router-view
    discard-changes-modal(v-if='showDiscardChangesModal')
    crop-image-modal(v-if='showCropImageModal')
    profile-toast(:item='toastMessage' isError :userIcon='userIcon')
</template>

<script>
import { mapState, mapActions } from 'vuex'
import get from 'lodash/get'

import KSelect from '@/components/KSelect'
import DiscardChangesModal from '@/components/Modals/DiscardChangesModal'
import ProfileToast from '@/components/Profile/ProfileToast'
import CropImageModal from '@/components/Modals/CropImageModal'

import editIcon from '@/assets/ic_edit.svg'
import lockIcon from '@/assets/ic_lock.svg'
import trashIcon from '@/assets/ic_trash.svg'
import defaultImg from '@/assets/profile_default_img.png'
import stopIcon from '@/assets/ic_stop.svg'

import { toLowerCamelCase } from '../utils/string'
import { getBase64, isImage, getExtension } from '../utils/file'
import {
  profileTabs,
  tabSchool,
  uploadFileBase,
  firstElement,
  maxFileSize,
  fullNameMaxLength
} from '../utils/constants'
import { createDiscardModalObject } from '../utils/object'
import { routes } from '../router'
import userRole from '../mixins/userRole'
import kreaRoles from '../mixins/kreaRoles'
import { authorTypes } from '../constants/krea'

export default {
  name: 'Profile',
  components: { DiscardChangesModal, KSelect, ProfileToast, CropImageModal },
  mixins: [userRole, kreaRoles],
  data() {
    return {
      editIcon,
      trashIcon,
      lockIcon,
      defaultImg,
      stopIcon,
      fullNameMaxLength,
      activeTab: '',
      toastDuration: 7000,
      fileBase64: { ...uploadFileBase },
      userIcon: false,
      modalToastDuration: 3000,
      optionMessage: 'changesSaved'
    }
  },
  computed: {
    ...mapState({
      userData: state => get(state.profile, 'userProfile.userVO') || {},
      profileEdition: state => state.profile.userProfileEdition,
      schoolGroupData: state => get(state.profile, 'userProfile.schoolGroupVO') || {},
      userPositions: state => state.profile.userPositions,
      showDiscardChangesModal: state => state.behavior.discardChangesModal.show,
      showCropImageModal: state => state.behavior.cropImageModal.show,
      editionModified: state => state.profile.editionModified,
      positionId: state => get(state.profile, 'userProfile.userVO.position.positionId') || '',
      errorOnSubmit: state => state.profile.errorOnSubmit,
      errors: state => state.profile.errors,
      selectedSchool: state => state.profile.selectedSchool
    }),
    tabs() {
      return profileTabs.filter(tab => !((tab === 'schoolGroup' && !this.isAdmin)))
    },
    schoolName() {
      return get(this.schoolGroupData, this.isAdmin ? 'name' : 'currentSchool.name')
    },
    position() {
      const userPositionId = get(this.userData, 'position.positionId')
      const positionObj = this.userPositions.find(pos => pos.positionId === userPositionId)
      return positionObj ? this.$t(`profile.position.${toLowerCamelCase(positionObj.position)}`) : ''
    },
    positionOptions() {
      return this.userPositions
        .filter(pos => pos.positionId)
        .map(option => {
          const { positionId, position } = option
          return {
            id: positionId,
            value: positionId,
            label: this.$t(`profile.position.${toLowerCamelCase(position)}`)
          }
        })
        .sort((a, b) => a.label.localeCompare(b.label))
    },
    isSchoolTrial() {
      return this.isSchoolTab && this.isTrial
    },
    preSelectedPositionId() {
      return this.userPositions.find(pos => pos.positionId === this.positionId).positionId
    },
    isEditView() {
      return this.$route.path.includes('edit')
    },
    isSchoolTab() {
      return this.activeTab === tabSchool
    },
    isTeacherInSchoolView() {
      return this.isTeacher && this.isSchoolTab
    },
    showEditButton() {
      return !this.isEditView && !this.isTeacherInSchoolView
    },
    editProfileMode() {
      return this.isEditView && this.$router.history.current.name === routes.myProfileEdit
    },
    missingFullName() {
      return this.editProfileMode && !this.profileEdition.fullName
    },
    shouldDisableSave() {
      return this.missingFullName
    },
    toastMessage() {
      return this.errorOnSubmit
        ? { message: this.$t(`profile.schoolTab.toastErrors.${this.errors.type}`) }
        : {}
    }
  },
  async created() {
    this.hydrateProfileView()
    if (this.selectedSchool.id) this.hydrateSchoolInfo(this.selectedSchool.id)
    if (this.$route.name === routes.school) {
      this.activeTab = routes.school
    } else if (this.$route.name === routes.schoolGroup) {
      this.activeTab = routes.schoolGroup
    } else {
      this.activeTab = routes.myProfile
    }
  },
  methods: {
    ...mapActions([
      'hydrateSchoolInfo',
      'hydrateProfileView',
      'hydrateProfile',
      'clearEdition',
      'updateProfileInfo',
      'saveSchoolEdition',
      'saveSchoolGroupEdition',
      'clearEdition',
      'showElement',
      'hideElement',
      'updateProfileField',
      'updateProfileInfoWithFile',
      'setEditionModified',
      'updateAuthorProfile',
      'uploadUserAvatar'
    ]),
    setActiveTab(tab) {
      if (this.editionModified) {
        this.showElement(createDiscardModalObject(this.clearEditionAndGoToDataView, {}))
      } else {
        this.activeTab = tab
        this.$router.push({ name: tab })
      }
    },
    goToEditView() {
      if (this.activeTab === routes.school) this.$router.push({ name: routes.schoolEdit })
      else if (this.activeTab === routes.schoolGroup) this.$router.push({ name: routes.schoolGroupEdit })
      else this.$router.push({ name: routes.myProfileEdit })
    },
    goToDataView() {
      if (this.activeTab === routes.school) this.$router.push({ name: routes.school })
      else if (this.activeTab === routes.schoolGroup) this.$router.push({ name: routes.schoolGroup })
      else this.$router.push({ name: routes.myProfile })
    },
    quitEditionView() {
      if (this.editionModified) this.showElement(createDiscardModalObject(this.clearEditionAndGoToDataView, {}))
      else this.goToDataView()
    },
    async save() {
      this.showElement({ element: 'fullPageLoader' })
      let saveAction, data
      if (this.fileBase64.content) {
        const { success, data } = await this.uploadUserAvatar({ ...this.fileBase64, userId: this.profileEdition.userId })
        if (!success) return this.$notify({ group: 'profile-edition', duration: this.toastDuration })
        else this.updateProfileField({ field: 'avatar', value: data })
      }
      if (this.activeTab === routes.school || this.activeTab === routes.schoolGroup) {
        saveAction = this.isAdmin
          ? this.saveSchoolGroupEdition
          : this.saveSchoolEdition
      } else {
        saveAction = this.updateProfileInfo
        data = this.profileEdition
      }
      if (this.hasKreaAccess) {
        await this.updateAuthorProfile({
          name: this.profileEdition.fullName,
          picture: this.profileEdition.avatar,
          bio: this.profileEdition.bio,
          type: authorTypes.schoolGroupCoAuthor
        })
      }
      await saveAction(data)
      if (this.errorOnSubmit) {
        this.$notify({ group: 'profile-edition', duration: this.toastDuration })
      } else {
        this.$notify({ group: 'profile-edition', clear: true })
        this.goToDataView()
      }
      this.hideElement({ element: 'fullPageLoader' })
    },
    clearEditionAndGoToDataView() {
      this.cleanImage()
      this.clearEdition()
      this.goToDataView()
      this.hideElement({ element: 'discardChangesModal' })
    },
    updateSelect(name, value) {
      const positionSeleted = this.userPositions.find(position => position.positionId === value)
      this.updateProfileField({ field: name, value: positionSeleted })
    },
    updateValue(e) {
      const valueWithOutSpaces = e.target.value.trim()
      this.updateProfileField({ field: e.target.name, value: valueWithOutSpaces })
    },
    cleanImage() {
      this.updateProfileField({ field: 'avatar', value: null })
      this.fileBase64 = { ...uploadFileBase }
    },
    updateBase64(file) {
      this.fileBase64.content = file
      this.fileBase64.fileType = 'png'
      this.setEditionModified()
    },
    async handleChangeImage(event) {
      const firstElementIndex = event.target.files[firstElement]
      const extension = getExtension(firstElementIndex.name)
      if (firstElementIndex.size <= maxFileSize && isImage(extension)) {
        const base64 = await getBase64(firstElementIndex)
        this.showElement({
          element: 'cropImageModal',
          props: {
            base64,
            width: 200,
            height: 200,
            resize: false,
            onSubmit: this.updateBase64
          }
        })
      } else {
        this.$notify({ group: 'custom-toast', duration: this.toastDuration })
      }
      this.$refs.profileImageInput.value = null
    },
    showMessage(message, role, value) {
      let text
      this.userIcon = !!role
      this.optionMessage = message
      if (!value) text = this.$t(`profile.schoolTab.${this.optionMessage}`)
      else text = this.$tc(`profile.schoolTab.${this.optionMessage}`, value === 1 ? value : 2)
      this.$notify({
        group: 'admin-team',
        duration: this.modalToastDuration,
        text
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/sizes';
@import 'src/scss/variables/colors';

.header {
  background: $blue url('../assets/bg_profile_header.svg') no-repeat center -120px;
  height: 382px;
  width: 100%;
  z-index: -1;
}

.max-width {
  width: 584px;
}

.content {
  background-color: $white;
  border: 1px solid $light-gray;
  border-radius: 8px;
  box-shadow: 0 2px 8px 0 $shadow-gray;
  margin: 85px 0;
  width: 885px;
}

.edit-button {
  width: 216px;
  padding: 0 12px;
}

.profile-img {
  background-color: $light-gray;
  border: 1px solid $light-gray;
  border-radius: 100%;
  height: 165px;
  width: 165px;
}

.profile {
  background-image: url('../assets/profile_bg_profile.svg');
  border-radius: 8px 8px 0 0;
  height: 252px;
  padding: 0 52px;
}

.profile-image-container {
  border-radius: 100%;
  height: 100%;
  opacity: 0;
  top: 0;

  &:hover {
    background-color: $shadow-white;
    opacity: 1;
  }
}

.tabs {
  padding: 0 52px;
}

.tab-selection {
  min-width: 232px;

  &::after {
    background-color: $light-gray;
    bottom: 2px;
    content: '';
    height: 3px;
    position: absolute;
    width: 100%;
  }
}

.tab-selector {
  padding-bottom: 20px;
  border-bottom: 5px solid transparent;

  &:hover {
    color: $blue;
  }

  &.active-tab {
    border-bottom: 5px solid $blue;
    color: $blue;
    font-weight: 700;
    z-index: 1;
  }

  &:first-child {
    max-width: 98px;
  }

  &:not(:first-child) {
    flex: 1;
    margin-left: 68px;
  }
}

.full-name-container {
  height: 96px;
}

.pointer{
  cursor: pointer;
}
</style>
